<template>
    <div class="course-list sidebar-list-wrapper" v-b-visible="maybeLoadFirstCourses">
        <!-- This is for screen readers -->
        <div class="sr-only" aria-live="assertive" aria-atomic="true">
            {{ searchStatus }}
        </div>

        <div class="sidebar-filter">
            <b-form-group id="filter-courses" label="Filter courses" class="filter-group">
                <input
                    id="filter-courses-input"
                    class="form-control"
                    v-model="filter"
                    ref="filter"
                />
            </b-form-group>
        </div>

        <ul class="sidebar-list no-alternating" v-if="sortedCourses.length > 0 || loading > 0">
            <template v-if="currentCourse">
                <li class="sidebar-list-section-header text-muted">
                    <small>Current course</small>
                </li>
                <li>
                    <course-list-item
                        :course="currentCourse"
                        :route-name="$route.name"
                        :current-id="currentCourse.id"
                        @open-menu="$emit('open-menu', $event)"
                    />
                </li>
                <li>
                    <hr class="separator" />
                </li>
            </template>
            <li v-if="loading > 0" class="loader-item">
                <cg-loader :scale="1" />
            </li>
            <template v-else-if="filteredCourses.length > 0">
                <course-list-item
                    v-for="course in filteredCourses.slice(0, coursesToShow)"
                    :key="`sorted-course-${course.id}`"
                    :route-name="$route.name"
                    :course="course"
                    :current-id="currentCourse && currentCourse.id"
                    @open-menu="$emit('open-menu', $event)"
                />
            </template>
            <li v-else class="text-center text-muted font-italic" role="status">
                No matching courses found
            </li>

            <li class="d-flex mx-2 my-1" v-if="showLoadMoreButton">
                <submit-button
                    class="flex-grow"
                    :submit="loadMoreCourses"
                    ref="loadMoreCoursesBtn"
                    variant="secondary"
                    v-b-visible="
                        v => {
                            loadMoreIsVisible = v;
                        }
                    "
                    :duration="0"
                    label="Load more courses"
                />
            </li>
        </ul>
        <span v-else class="sidebar-list no-items-text"> You don't have any courses yet. </span>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import SubmitButton from '@/components/SubmitButton';
import { INITIAL_COURSES_AMOUNT } from '@/constants';

import CourseListItem from './CourseListItem';

let idNum = 0;

export default {
    name: 'course-list',

    props: {
        data: {
            type: Object,
            default: null,
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const id = idNum++;
        return {
            filter: '',
            addButtonId: `course-add-btn-${id}`,
            popoverId: `course-add-popover-${id}`,
            loading: false,
            // Is the load more button visible? Only releveant when `showLoadMoreButton` is `true`.
            loadMoreIsVisible: false,
            coursesToShow: INITIAL_COURSES_AMOUNT,
            searchStatus: '',
        };
    },

    computed: {
        ...mapGetters('courses', [
            'sortedCourses',
            'getCourse',
            'retrievedAllCourses',
            'searchCourses',
        ]),

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        filteredCourses() {
            const result = this.searchCourses(this.filter);

            if (result.length === 0) {
                this.searchStatus = 'No matching courses found. Clear field and try again.';
            } else {
                this.searchStatus = '';
            }

            return result;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        currentCourseId() {
            return this.$routeParamAsId('courseId');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        currentCourse() {
            return this.getCourse(this.currentCourseId).extract();
        },

        /** @returns If we should show the load more courses button. */
        showLoadMoreButton() {
            if (this.loading > 0) {
                return false;
            }
            if (this.filteredCourses.length > this.coursesToShow) {
                return true;
            }

            return !this.retrievedAllCourses;
        },
    },

    watch: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        currentCourseId() {
            this.loadCurrentCourse();
        },

        /** Load more courses when the button becomes visible. */
        loadMoreIsVisible() {
            this.maybeClickLoadMoreCourses();
        },

        /** Load more courses when the load more courses button appears. */
        showLoadMoreButton() {
            this.maybeClickLoadMoreCourses();
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async mounted() {
        this.$root.$on('sidebar::reload', this.reload);

        await this.asLoader(this.loadCurrentCourse());
        await this.$nextTick();

        const activeEl = document.activeElement;
        if (
            !activeEl ||
            !activeEl.matches('input, textarea') ||
            activeEl.closest('.sidebar .submenu')
        ) {
            this.$refs.filter.focus();
        }
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    destroyed() {
        this.loadMoreIsVisible = false;
        this.$root.$off('sidebar::reload', this.reload);
    },

    methods: {
        ...mapActions('courses', [
            'loadFirstCourses',
            'loadSingleCourse',
            'loadNextCourses',
            'reloadCourses',
        ]),

        /** Load more if we are showing the load more button. */
        maybeClickLoadMoreCourses() {
            if (this.loadMoreIsVisible && this.showLoadMoreButton) {
                this.$withRef('loadMoreCoursesBtn', btn => {
                    btn.onClick();
                });
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async asLoader(promise) {
            if (this.loading === 0) {
                this.$emit('loading');
            }

            this.loading += 1;
            try {
                await promise;
            } finally {
                this.loading = Math.max(0, this.loading - 1);
            }

            if (this.loading === 0) {
                this.$emit('loaded');
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeLoadFirstCourses(doLoad) {
            if (doLoad) {
                this.asLoader(this.loadFirstCourses());
            }
        },

        /** Reload the entire courses store. */
        reload() {
            this.loadMoreIsVisible = false;
            this.asLoader(
                this.reloadCourses({ fullReload: false }).then(() => this.loadCurrentCourse()),
            );
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        closePopover() {
            this.$root.$emit('bv::hide::popover', this.popoverId);
        },

        /**
         * Load more courses if needed.
         *
         * @returns An empty object.
         */
        async loadMoreCourses() {
            await this.loadFirstCourses();
            await this.$afterRerender();

            // Load more until we hide the button or until the button is no longer visible.
            while (this.loadMoreIsVisible && this.showLoadMoreButton) {
                if (this.coursesToShow >= this.filteredCourses.length) {
                    // eslint-disable-next-line no-await-in-loop
                    await this.loadNextCourses({ amount: INITIAL_COURSES_AMOUNT });
                }
                this.coursesToShow = Math.min(
                    this.coursesToShow + INITIAL_COURSES_AMOUNT,
                    this.filteredCourses.length,
                );
                // eslint-disable-next-line no-await-in-loop
                await this.$afterRerender();
            }

            return {};
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadCurrentCourse() {
            if (this.currentCourseId) {
                return await this.loadSingleCourse({ courseId: this.currentCourseId });
            }
            return null;
        },
    },

    components: {
        CourseListItem,
        SubmitButton,
    },
};
</script>
